import React, { useState, useEffect } from "react";
import Loading from "../loading";
import { fetchOrderHistory } from "../../../node_modules/shops-query/src/modules/OrderHistory/Controller/index.js";
import { imagePrefix, getUserId, shopid } from "../../utils/url.js";
import { useNavigate } from "react-router-dom";

const MyOrders = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [activeSummary, setActiveSummary] = useState(null);
  const userId = getUserId();

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setOrders([]);
      return;
    }

    const fetchOrderData = async () => {
      window.scrollTo(0, 0);

      try {
        setLoading(true);
        const orderData = await fetchOrderHistory(
          Number(userId),
          Number(shopid)
        );
        setOrders(orderData);
        setError(null);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError(err.message || "Failed to fetch orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [userId]);

  const getStatusText = (s) => {
    switch (s) {
      case 1:
        return "Waiting For Approval";
      case 2:
        return "Order Accepted";
      case 3:
        return "Waiting For Pickup";
      case 4:
        return "Order Shipped";
      case 5:
        return "Delivered";
      default:
        return "Cancelled";
    }
  };

  const calculateOrderTotal = (order) => {
    const itemTotal = order.orderdetails.reduce((total, item) => {
      const iTotal = item.price * item.quantity;
      const tax = (iTotal * item.tax) / 100;
      return total + iTotal + tax;
    }, 0);

    const shipping = Number(order.deliveryCharge) || Number(order.shippingCost) || 0;

    return {
      itemTotal,
      shipping,
      total: itemTotal + shipping
    };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  };

  const toggleRowExpand = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (!userId) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white font-inter">
        <div className="text-center">
          <h2 className="text-[#555555] text-2xl mb-6">
            Please login to view your orders
          </h2>
          <button className="bg-[#4654DB] text-white px-8 py-3 rounded font-bold">
            LOGIN
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-[650px] flex items-center justify-center font-inter">
        <Loading color="#10B981" size="xl" text="Loading your orders..." />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">Error: {error}</div>;
  }

  if (orders.length === 0) {
    return (
      <div className="w-full mx-auto px-4 py-10 text-center md:mb-64 mb-32 ">
        <h1 className="text-2xl  md:ml-12 font-bold mb-6 text-left text-black">MY ORDERS</h1>

        <p className="text-lg text-gray-600 leading-relaxed md:mt-32 mt-16">
          You haven’t placed any orders yet.
          <br />
          Once you do, they’ll show up here.
        </p>

        <button
          onClick={() => navigate("/")}
          className="mt-6 border-2 border-[#F74F25] text-[#F74F25] px-6 py-3 rounded font-bold uppercase hover:bg-[#F74F25] hover:text-white transition"
        >
          Browse Collections
        </button>
      </div>
    );
  }

  // -------------------------------
  //        MAIN ORDERS VIEW
  // -------------------------------
  return (
    <div className="max-w-8xl mx-auto px-4 md:px-16 py-8 font-inter md:mb-32">
      <p className="font-bold text-[28px] text-black mb-6">MY ORDERS</p>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block">
        <div className="border border-[#EEEEEE] rounded-lg">
          <table className="min-w-full">
            <thead className="bg-[#FFE9E4] text-[18px] text-[#313345] font-bold">
              <tr>
                <th className="px-6 py-3 text-left">ORDER NO</th>
                <th className="px-6 py-3 text-left">ORDER DATE</th>
                <th className="px-6 py-3 text-left">NET PAYABLE</th>
                <th className="px-6 py-3 text-left">ADDRESS</th>
                <th className="px-6 py-3 text-left">MOBILE</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-[#EEEEEE]">
              {orders.map((order) => {
                const { total, itemTotal, shipping } = calculateOrderTotal(order);

                return (
                  <React.Fragment key={order.id}>
                    <tr className="cursor-pointer font-medium text-[#101011]">
                      <td
                        className="px-6 py-4 underline"
                        onClick={() => toggleRowExpand(order.id)}
                      >
                        {expandedRows.includes(order.id) ? "-" : "+"} #{order.id}
                      </td>

                      <td className="px-6 py-4">
                        {formatDate(order.timestamp)}
                      </td>

                      <td
                        className="px-6 py-4 relative"
                      >
                        <div
                          className="underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveSummary(activeSummary === order.id ? null : order.id);
                          }}
                        >
                          ₹ {Math.round(total)}
                        </div>

                        {activeSummary === order.id && (
                          <div
                            className="absolute top-full left-0 mt-2 w-[250px] bg-white shadow-xl rounded-lg z-50 border border-gray-100 animate-fadeIn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Caret pointing up */}
                            <div className="absolute left-6 -top-2 w-4 h-4 bg-[#101011] transform rotate-45"></div>

                            <div className="bg-[#101011] text-white text-sm font-bold p-3 rounded-t-lg">
                              Summary
                            </div>
                            <div className="p-4 text-sm text-[#424242] space-y-2">
                              <div className="flex justify-between">
                                <span>Item Price:</span>
                                <span className="font-bold">₹ {Math.round(itemTotal)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Shipping Charges:</span>
                                <span className="font-bold">₹ {Math.round(shipping)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 max-w-xs truncate text-[#555555]">
                        {order.customerAddress}
                      </td>

                      <td className="px-6 py-4 text-[#555555]">{order.customerMobile}</td>
                    </tr>

                    {expandedRows.includes(order.id) && (
                      <tr className="">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="grid grid-cols-5 font-bold bg-[#F4F4F4] text-center py-2 border-b text-sm text-[#555555]">
                            <div>IMAGE</div>
                            <div>DETAILS</div>
                            <div>QTY</div>
                            <div>STATUS</div>
                            <div>AMOUNT</div>
                          </div>

                          {order.orderdetails.map((item) => (
                            <div
                              key={item.id}
                              className="grid grid-cols-5 text-center py-3 border-b text-sm items-center"
                            >
                              <div className="flex justify-center">
                                <img
                                  src={
                                    item.Products?.[0]?.featureImage
                                      ? `${imagePrefix}${item.Products[0].featureImage}`
                                      : "/placeholder-product.png"
                                  }
                                  className="w-16 h-16 object-cover rounded"
                                />
                              </div>

                              <div className="font-medium text-[#101011]">{item.Products?.[0]?.name}</div>

                              <div className="text-[#555555]">{item.quantity}</div>

                              <div className="text-green-600 font-semibold">{getStatusText(item.status)}</div>

                              <div className="font-bold text-[#101011]">₹ {Math.round(item.price * item.quantity + (item.price * item.quantity * item.tax) / 100)}</div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE VIEW (Cards) */}
      <div className="md:hidden space-y-4 mt-4">
        {orders.map((order) => {
          const { total, itemTotal, shipping } = calculateOrderTotal(order);

          // mobile expand toggle
          const isExpanded = expandedRows.includes(order.id);
          const first = order.orderdetails[0];

          return (
            <div
              key={order.id}
              className="bg-white rounded-lg p-4 shadow border border-[#EEEEEE]"
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-lg text-[#101011]">Order #{order.id}</h3>
                <span className="text-sm text-green-600 font-semibold">
                  {getStatusText(first?.status)}
                </span>
              </div>

              {/* First Product */}
              <div className="flex gap-4 mb-3">
                <img
                  src={
                    first?.Products?.[0]?.featureImage
                      ? `${imagePrefix}${first.Products[0].featureImage}`
                      : "/placeholder-product.png"
                  }
                  className="w-20 h-20 object-cover rounded"
                />

                <div className="flex-1 relative">
                  <p className="font-medium text-[#101011]">{first?.Products?.[0]?.name}</p>
                  <p className="text-xs text-gray-500">
                    Qty: {first?.quantity}
                  </p>

                  <div
                    className="font-bold mt-1 inline-block border-b border-underline border-gray-400 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveSummary(activeSummary === order.id ? null : order.id);
                    }}
                  >
                    ₹ {Math.round(total)}
                  </div>

                  {activeSummary === order.id && (
                    <div
                      className="absolute top-full left-0 mt-2 w-[220px] bg-white shadow-xl rounded-lg z-50 border border-gray-100 animate-fadeIn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Caret pointing up */}
                      <div className="absolute left-4 -top-2 w-4 h-4 bg-[#101011] transform rotate-45"></div>

                      <div className="bg-[#101011] text-white text-xs font-bold p-2 rounded-t-lg">
                        Summary
                      </div>
                      <div className="p-3 text-xs text-[#424242] space-y-2">
                        <div className="flex justify-between">
                          <span>Item Price:</span>
                          <span className="font-bold">₹ {Math.round(itemTotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Shipping Charges:</span>
                          <span className="font-bold">₹ {Math.round(shipping)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Expand Button if more than 1 item */}
              {order.orderdetails.length > 1 && (
                <button
                  onClick={() => toggleRowExpand(order.id)}
                  className="text-blue-600 underline text-sm mb-3"
                >
                  {isExpanded
                    ? "Hide items"
                    : `View all items (${order.orderdetails.length})`}
                </button>
              )}

              {/* Expanded Item List */}
              {isExpanded && (
                <div className="space-y-3 mb-3">
                  {order.orderdetails.slice(1).map((item) => {
                    const prod = item.Products?.[0];
                    return (
                      <div
                        key={item.id}
                        className="flex gap-4 p-2 bg-gray-50 rounded"
                      >
                        <img
                          src={
                            prod?.featureImage
                              ? `${imagePrefix}${prod.featureImage}`
                              : "/placeholder-product.png"
                          }
                          className="w-16 h-16 object-cover rounded"
                        />

                        <div className="flex-1">
                          <p className="font-medium text-sm">{prod?.name}</p>
                          <p className="text-xs text-gray-600">
                            Qty: {item.quantity}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-sm text-gray-600">
                Order Date: {formatDate(order.timestamp)}
              </p>
              <p className="text-sm text-gray-600">
                Address: {order.customerAddress}
              </p>

              {/* <button className="mt-4 w-full bg-[#F74F25] text-white py-2 rounded font-semibold">
                BUY AGAIN
              </button> */}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyOrders;
